import {
  AlertTriangle,Bug,
  CheckCircle2,
  ClipboardCheck,
  Cloud,
  FileText,
  Gauge,
  GitCompare,
  HelpCircle,
  KeyRound,
  LayoutGrid,
  Lightbulb,
  Lock,
  Network,
  PieChart,
  Search,
  Server,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../ui/utils";

// Every suggestion the panel shows is written by the model for the page the user is on. The five
// hardcoded starters that used to live here — compliance posture, asset inventory, audit readiness,
// top risks, non-compliant controls — were substituted whenever the backend returned nothing, which
// meant a failed generation rendered as five confident questions this product never wrote, identically
// on every page. They are gone: an empty response now carries the reason it is empty, and the panel
// says so.

const ICON_RULES: Array<[RegExp, LucideIcon]> = [
  [/\b(vuln\w*|cve|exploit|finding)/i, Bug],
  [/\b(risk|threat)/i, AlertTriangle],
  [/\b(trend|over time|history|timeline|monthly|weekly|this week|since|changed|change)/i, TrendingUp],
  [/\b(audit|evidence|readiness|ready)/i, ClipboardCheck],
  [/\b(non[-\s]?compliant|failing|failed|fail|violation|gap)/i, ShieldAlert],
  [/\b(passing|passed|remediat|resolved)/i, CheckCircle2],
  [/\b(control|policy|policies|requirement)/i, ShieldCheck],
  [/\b(asset|inventor|cmdb|device|endpoint|host|server|machine)/i, Server],
  [/\b(framework|catalog|standard|section|group)/i, LayoutGrid],
  [/\b(compare|comparison|vs|versus)/i, GitCompare],
  [/\b(chart|graph|breakdown|distribution|visual|pie|donut|doughnut)/i, PieChart],
  [/\b(access|mfa|identity|login|auth\w*|permission|privileg|role|user)/i, KeyRound],
  [/\b(encrypt\w*|crypto\w*|tls|ssl|certificate|cipher)/i, Lock],
  [/\b(cloud|aws|azure|gcp|s3|bucket)/i, Cloud],
  [/\b(network|firewall|traffic|port|segment)/i, Network],
  [/\b(report|export|document|artifact)/i, FileText],
  [/\b(insight|recommend\w*|suggest\w*|summar\w*|advice)/i, Lightbulb],
  [/\b(posture|compliance|compliant|score|coverage|status)/i, Gauge],
  [/\b(search|find|which|what|show|list|how|where)/i, Search],
];

function iconForText(text: string): LucideIcon {
  for (const [re, icon] of ICON_RULES) {
    if (re.test(text)) return icon;
  }
  return HelpCircle;
}

/**
 * `pill` is the original inline chip, used for the follow-up questions under an assistant answer where
 * several sit side by side and brevity matters.
 *
 * `row` is the full-width form used for panel-open suggestions. They are longer, fewer, and read as a
 * menu of things to do rather than as tags — so they get one per line, a tinted icon and a chevron
 * affording the click.
 */
type SuggestionChipVariant = "pill" | "row";

export function SuggestionChip({
  icon,
  title,
  onClick,
  disabled = false,
  variant = "pill",
  className,
}: {
  icon?: LucideIcon;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: SuggestionChipVariant;
  className?: string;
}) {
  const Icon = icon ?? iconForText(title);

  if (variant === "row") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "group flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all duration-200",
          "border-border/70 bg-card/85 text-foreground/90",
          "hover:border-primary/45 hover:bg-primary/[0.06] hover:text-foreground hover:shadow-sm",
          "active:scale-[0.99] motion-reduce:transform-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          "dark:border-white/10 dark:bg-white/[0.08] dark:text-foreground/85 dark:hover:border-violet-300/40 dark:hover:bg-white/[0.12]",
          "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:shadow-none",
          className,
        )}
      >
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary/15"
          aria-hidden
        >
          <Icon className="size-4" strokeWidth={2} />
        </span>
        {/* Wraps rather than truncating — long questions must stay fully readable. flex-1 means it
            reclaims the space where a trailing chevron used to sit. */}
        <span className="min-w-0 flex-1 break-words text-[0.8125rem] font-medium leading-snug">
          {title}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-left align-top text-[0.75rem] font-medium leading-snug transition-all duration-200",
        // No backdrop-blur on chips: a backdrop-filter repaints every chip on every scroll frame.
        "border-border/70 bg-card/85 text-foreground/85",
        "hover:-translate-y-px hover:border-primary/45 hover:bg-primary/[0.06] hover:text-foreground hover:shadow-sm",
        "active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        "dark:border-white/10 dark:bg-white/[0.08] dark:text-foreground/80 dark:hover:border-violet-300/40 dark:hover:bg-white/[0.12]",
        "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-none",
        className,
      )}
    >
      <Icon
        className="size-3.5 shrink-0 text-primary/70 transition-colors duration-200 group-hover:text-primary"
        strokeWidth={2}
        aria-hidden
      />
      <span className="min-w-0 break-words">{title}</span>
    </button>
  );
}

/** Placeholder rows at the real row height, so the panel does not jump when suggestions land. */
export function SuggestionRowSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-1.5" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex w-full items-center gap-2.5 rounded-xl border border-border/60 bg-card/50 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]"
        >
          <span className="size-7 shrink-0 animate-pulse rounded-lg bg-primary/10" />
          <span className="h-3.5 flex-1 animate-pulse rounded bg-muted-foreground/15" />
        </div>
      ))}
    </div>
  );
}
